import React, { useState, useEffect, useRef, useMemo } from 'react';
import './Cart.css';
import CashLimitPopup from './CashLimitPopup';
import CartReviewModal from './CartReviewModal';
import { BACKEND_URL } from '../config';

const Cart = ({
  cartItems,
  removeFromCart,
  checkout = () => {},
  togglePaymentType,
  openEmailValuationForm,
  userSettings,
  clearCart,
  onSaveForLater = () => {},
  onLoadBuylist = () => {},
  calculatePrice = () => ({}),
  allowedConditions = [],
  setCartItems = () => {},
  hideCheckoutButton = false,
  customItems: customItemsProp = [], // OPTIMIZATION: Accept custom items from parent
  isLoadingCustomItems: isLoadingCustomItemsProp = false,
  paymentSplitData,
  setPaymentSplitData
}) => {
  // Function to update cart item details (condition, printing, etc.)
  const updateCartItemDetails = (updatedItem) => {
    // Create a direct setter function to update the cart items
    setCartItems(prevItems => prevItems.map(item => 
      item.cartItemId === updatedItem.cartItemId ? updatedItem : item
    ));
  };
  const [showCashLimitPopup, setShowCashLimitPopup] = useState(false);
  const [cashLimitConfirmed, setCashLimitConfirmed] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // OPTIMIZATION: Use custom items from props instead of fetching here (eliminates duplicate API call)
  const customItems = customItemsProp;
  const isLoadingCustomItems = isLoadingCustomItemsProp;

  const [showCustomItems, setShowCustomItems] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  // Payment split local state
  const [isPaymentSplitExpanded, setIsPaymentSplitExpanded] = useState(false);
  const [cashAmountInput, setCashAmountInput] = useState('');
  const [creditAmountInput, setCreditAmountInput] = useState('');

  // Derived values from payment split data
  const isPriceSplitAllowed = userSettings?.priceSplit?.enabled !== false;
  const canUsePaymentSplit = isPriceSplitAllowed && typeof setPaymentSplitData === 'function';
  const cashPercentage = paymentSplitData?.cashPercentage || 0;
  const isPaymentSplitEnabled = isPriceSplitAllowed && (paymentSplitData?.isEnabled || false);

  // OPTIMIZATION: Single-pass cart totals calculation with useMemo
  // Replaces 4 separate reduce() operations with one memoized calculation
  const cartTotals = useMemo(() => {
    let cashSubtotal = 0;
    let creditSubtotal = 0;
    let totalCashPrice = 0;
    let totalCreditPrice = 0;

    for (const item of cartItems) {
      const cashPrice = parseFloat(item.cashPrice || 0);
      const creditPrice = parseFloat(item.creditPrice || 0);

      totalCashPrice += cashPrice;
      totalCreditPrice += creditPrice;

      if (item.isCash) {
        cashSubtotal += cashPrice;
      } else {
        creditSubtotal += creditPrice;
      }
    }

    return {
      cashSubtotal,
      creditSubtotal,
      total: cashSubtotal + creditSubtotal,
      totalCashPrice,
      totalCreditPrice
    };
  }, [cartItems]);

  // Destructure for backwards compatibility
  const { cashSubtotal: itemCashSubtotal, creditSubtotal: itemCreditSubtotal, total: itemTotal, totalCashPrice, totalCreditPrice } = cartTotals;

  // Calculate payment split amounts based on percentage (only if enabled)
  const splitCashAmount = isPaymentSplitEnabled
    ? (totalCashPrice * cashPercentage / 100)
    : 0;
  const splitCreditAmount = isPaymentSplitEnabled
    ? (totalCreditPrice * (100 - cashPercentage) / 100)
    : 0;
  const splitTotal = isPaymentSplitEnabled
    ? (splitCashAmount + splitCreditAmount)
    : 0;

  const cashSubtotal = isPaymentSplitEnabled ? splitCashAmount : itemCashSubtotal;
  const creditSubtotal = isPaymentSplitEnabled ? splitCreditAmount : itemCreditSubtotal;
  const total = isPaymentSplitEnabled ? splitTotal : itemTotal;

  // Check if cash limit is exceeded
  useEffect(() => {
    // Only check if we have user settings and there are items in the cart
    if (userSettings && cartItems.length > 0) {
      const cashLimit = userSettings.cash_limit;
      
      // If cash_limit is defined, not 0, and the cash subtotal exceeds it
      if (cashLimit !== undefined && cashLimit !== 0 && cashSubtotal > cashLimit) {
        // Only show the popup if it hasn't been confirmed yet
        if (!cashLimitConfirmed) {
          setShowCashLimitPopup(true);
        }
      } else {
        // Reset if the cash subtotal goes back below the limit
        setShowCashLimitPopup(false);
      }
    }
  }, [cashSubtotal, userSettings, cartItems, cashLimitConfirmed]);
  
  const handleCashLimitPopupClose = (confirmed) => {
    setShowCashLimitPopup(false);
    if (confirmed) {
      setCashLimitConfirmed(true);
    }
  };

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const currencySymbol = currencySymbols[userSettings?.currency || 'USD'] || '$';

  const handleClearCart = () => {
    console.log('Clear All button clicked');
    setShowClearConfirmation(true);
  };

  const confirmClearCart = () => {
    clearCart();
    setShowClearConfirmation(false);
  };

  const cancelClearCart = () => {
    setShowClearConfirmation(false);
  };

  const allItemsCash = cartItems.length > 0 && cartItems.every(item => item.isCash);
  const allItemsCredit = cartItems.length > 0 && cartItems.every(item => !item.isCash);

  const switchAllPaymentTypes = (useCash) => {
    if (typeof togglePaymentType !== 'function') return;

    cartItems
      .filter(item => item.isCash !== useCash)
      .forEach(item => togglePaymentType(item));
  };

  // Payment Split Functions
  const updatePaymentSplit = (newCashPercentage) => {
    if (!canUsePaymentSplit) return;

    const cashPercentageDecimal = newCashPercentage / 100;
    const creditPercentageDecimal = (100 - newCashPercentage) / 100;

    const cashAmount = totalCashPrice * cashPercentageDecimal;
    const creditAmount = totalCreditPrice * creditPercentageDecimal;

    setPaymentSplitData({
      cashPercentage: newCashPercentage,
      creditPercentage: 100 - newCashPercentage,
      cashAmount,
      creditAmount,
      isEnabled: true
    });
  };

  const togglePaymentSplit = () => {
    if (!canUsePaymentSplit) return;

    const newExpanded = !isPaymentSplitExpanded;
    setIsPaymentSplitExpanded(newExpanded);

    if (newExpanded && !isPaymentSplitEnabled) {
      // When expanding for the first time, initialize with 50/50 split
      updatePaymentSplit(50);
    } else if (!newExpanded) {
      // When collapsing, disable the split and reset to defaults
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
    }
  };

  const handleCashPercentageChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      updatePaymentSplit(value);
      const newCashAmount = (totalCashPrice * value / 100).toFixed(2);
      setCashAmountInput(newCashAmount);
      const newCreditAmount = (totalCreditPrice * (100 - value) / 100).toFixed(2);
      setCreditAmountInput(newCreditAmount);
    }
  };

  const handleCashPercentageInputChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      updatePaymentSplit(numValue);
      const newCashAmount = (totalCashPrice * numValue / 100).toFixed(2);
      setCashAmountInput(newCashAmount);
      const newCreditAmount = (totalCreditPrice * (100 - numValue) / 100).toFixed(2);
      setCreditAmountInput(newCreditAmount);
    }
  };

  const handleCashPercentageInputBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) {
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (value > 100) {
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
      setCreditAmountInput('0.00');
    }
  };

  const handleCashAmountChange = (e) => {
    setCashAmountInput(e.target.value);
  };

  const handleCashAmountBlur = () => {
    const numValue = parseFloat(cashAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCashAmountInput('0.00');
      updatePaymentSplit(0);
      setCreditAmountInput(totalCreditPrice.toFixed(2));
    } else if (numValue > totalCashPrice) {
      setCashAmountInput(totalCashPrice.toFixed(2));
      updatePaymentSplit(100);
      setCreditAmountInput('0.00');
    } else {
      const percentage = Math.min(100, Math.max(0, (numValue / totalCashPrice) * 100));
      updatePaymentSplit(percentage);
      setCashAmountInput(numValue.toFixed(2));
      const newCreditAmount = (totalCreditPrice * (100 - percentage) / 100).toFixed(2);
      setCreditAmountInput(newCreditAmount);
    }
  };

  const handleCreditAmountChange = (e) => {
    setCreditAmountInput(e.target.value);
  };

  const handleCreditAmountBlur = () => {
    const numValue = parseFloat(creditAmountInput);
    if (isNaN(numValue) || numValue < 0) {
      setCreditAmountInput('0.00');
      updatePaymentSplit(100);
      setCashAmountInput(totalCashPrice.toFixed(2));
    } else if (numValue > totalCreditPrice) {
      setCreditAmountInput(totalCreditPrice.toFixed(2));
      updatePaymentSplit(0);
      setCashAmountInput('0.00');
    } else {
      const creditPercentage = Math.min(100, Math.max(0, (numValue / totalCreditPrice) * 100));
      const cashPct = 100 - creditPercentage;
      updatePaymentSplit(cashPct);
      setCreditAmountInput(numValue.toFixed(2));
      const newCashAmount = (totalCashPrice * cashPct / 100).toFixed(2);
      setCashAmountInput(newCashAmount);
    }
  };

  // Sync amount inputs when cart changes or percentage changes
  useEffect(() => {
    if (!isPriceSplitAllowed && paymentSplitData?.isEnabled && typeof setPaymentSplitData === 'function') {
      setPaymentSplitData({
        cashPercentage: 0,
        creditPercentage: 100,
        cashAmount: 0,
        creditAmount: 0,
        isEnabled: false
      });
      setIsPaymentSplitExpanded(false);
      return;
    }

    if (isPaymentSplitEnabled) {
      const newCashAmount = (totalCashPrice * cashPercentage / 100).toFixed(2);
      const newCreditAmount = (totalCreditPrice * (100 - cashPercentage) / 100).toFixed(2);
      setCashAmountInput(newCashAmount);
      setCreditAmountInput(newCreditAmount);
    } else {
      setCashAmountInput('0.00');
      setCreditAmountInput('0.00');
    }
  }, [totalCashPrice, totalCreditPrice, cashPercentage, isPaymentSplitEnabled, isPriceSplitAllowed, paymentSplitData?.isEnabled, setPaymentSplitData]);

  // Reference to the file input element
  const fileInputRef = useRef(null);

  // Function to trigger the file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };
  
  // Function to import cart items from CSV
  const importFromCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const lines = csvContent.split('\n');
        
        // Skip header row and empty lines
        const dataRows = lines.filter((line, index) => index > 0 && line.trim() !== '');
        
        // Skip the last row if it's a summary row (contains "Total:")
        const itemRows = dataRows.filter(row => !row.includes('"Total:"'));
        
        // Parse CSV rows into cart items
        const importedItems = itemRows.map((row, index) => {
          // Split by comma, but respect quoted values
          const values = [];
          let inQuotes = false;
          let currentValue = '';
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            
            if (char === '"') {
              if (inQuotes && row[i + 1] === '"') {
                // Handle escaped quotes
                currentValue += '"';
                i++;
              } else {
                // Toggle quote state
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              // End of value
              values.push(currentValue);
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          
          // Add the last value
          values.push(currentValue);
          
          // Extract values from the row
          const [name, condition, printing, language, quantity, priceType, price] = values;
          
          // Create a cart item object
          return {
            name: name.replace(/^"|"$/g, ''),
            condition: condition.replace(/^"|"$/g, ''),
            printing: printing.replace(/^"|"$/g, ''),
            language: language.replace(/^"|"$/g, ''),
            quantity: parseInt(quantity.replace(/^"|"$/g, ''), 10) || 1,
            isCash: priceType.replace(/^"|"$/g, '') === 'Cash',
            cashPrice: priceType.replace(/^"|"$/g, '') === 'Cash' ? price.replace(/^"|"$/g, '') : '0',
            creditPrice: priceType.replace(/^"|"$/g, '') === 'Credit' ? price.replace(/^"|"$/g, '') : '0',
            cartItemId: `imported-${index}-${Date.now()}` // Generate a unique ID
          };
        });
        
        // Set the cart items
        if (importedItems.length > 0) {
          setCartItems(importedItems);
          alert(`Successfully imported ${importedItems.length} items from CSV.`);
        } else {
          alert('No valid items found in the CSV file.');
        }
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('Error importing CSV file. Please check the file format.');
      }
      
      // Reset the file input
      event.target.value = '';
    };
    
    reader.onerror = () => {
      alert('Error reading the CSV file.');
      // Reset the file input
      event.target.value = '';
    };
    
    reader.readAsText(file);
  };
  
  // Function to export cart items to CSV
  const exportToCSV = () => {
    // Skip if cart is empty
    if (cartItems.length === 0) return;

    // Create CSV header - exactly 8 columns
    // Note: quantity is always 1 now (separate line items)
    const csvHeader = ['Name', 'Condition', 'Printing', 'Language', 'Quantity', 'Price Type', 'Price', 'Total'];

    // Create CSV rows from cart items - ensure exactly 8 columns
    const csvRows = cartItems.map(item => {
      const price = item.isCash ? item.cashPrice : item.creditPrice;

      // For video games, show variant and console instead of condition/printing
      const condition = item.isVideoGame ? (item.variant?.toUpperCase() || 'CIB') : item.condition;
      const printing = item.isVideoGame ? (item.consoleName || 'Video Game') : item.printing;

      // Ensure exactly 8 columns
      return [
        String(item.name || 'Unknown'),
        condition,
        printing,
        item.language || 'EN',
        1, // quantity is always 1 now
        item.isCash ? 'Cash' : 'Credit',
        price, // Just the price without currency symbol
        price // Total is the same as price since quantity is 1
      ];
    });
    
    // Add a summary row - ensure exactly 8 columns
    csvRows.push(['', '', '', '', '', '', 'Total:', total.toFixed(2)]);
    
    // Convert to CSV string - ensure proper escaping and no extra columns
    const csvContent = [
      csvHeader.join(','),
      ...csvRows.map(row => {
        // Ensure each row has exactly 8 columns
        const paddedRow = row.slice(0, 8); // Take only the first 8 columns
        while (paddedRow.length < 8) paddedRow.push(''); // Pad with empty strings if needed
        
        // Properly escape and quote each cell
        return paddedRow.map(cell => {
          // Convert to string and escape quotes
          const cellStr = String(cell).replace(/"/g, '""');
          return `"${cellStr}"`;
        }).join(',');
      })
    ].join('\n');
    
    // Create a Blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Set up download attributes
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    link.setAttribute('href', url);
    link.setAttribute('download', `cart-export-${date}.csv`);
    link.style.visibility = 'hidden';
    
    // Append to document, trigger download, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="cart">
      {showCashLimitPopup && (
        <CashLimitPopup
          onClose={handleCashLimitPopupClose}
          cashLimit={userSettings?.cash_limit}
          cashSubtotal={cashSubtotal}
          simplistic={userSettings?.use_simplistic_template === true || userSettings?.template_version === 'simplistic'}
          currencySymbol={currencySymbol}
        />
      )}
      
      {/* Clear Cart Confirmation Dialog */}
      {showClearConfirmation && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Clear Cart</h3>
            <p>Are you sure you want to clear all items from your cart? This action cannot be undone.</p>
            <div className="popup-buttons">
              <button className="popup-button popup-button-cancel" onClick={cancelClearCart}>
                Cancel
              </button>
              <button className="popup-button popup-button-confirm" onClick={confirmClearCart}>
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="cart-header">
        <h2 className="cart-title">Your Cart</h2>
        <div className="cart-header-buttons">
          {!hideCheckoutButton && (
            <>
              <button
                className="cart-header-button load-buylist-btn"
                onClick={onLoadBuylist}
                title="Load a previously saved buylist"
              >
                📂 Load
              </button>
              <button
                className="cart-header-button import-csv-btn"
                onClick={triggerFileInput}
                title="Import items from CSV file"
              >
                📥 Import
              </button>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".csv"
                onChange={importFromCSV}
              />
              <button
                className="cart-header-button export-csv-btn"
                onClick={exportToCSV}
                disabled={cartItems.length === 0}
                title="Export cart to CSV file"
              >
                📤 Export
              </button>
              <button
                className="cart-header-button review-cart-btn"
                onClick={() => setShowReviewModal(true)}
                disabled={cartItems.length === 0}
                title="Review consolidated cart"
              >
                📋 Review
              </button>
            </>
          )}
          <button className="cart-header-button clear-cart-btn" onClick={handleClearCart} title="Clear all items">
            🗑️ Clear
          </button>
        </div>
      </div>

      {cartItems.length > 0 && (
        <div className="cart-payment-actions" aria-label="Switch all payment types">
          <button
            type="button"
            className="switch-all-button switch-all-cash"
            onClick={() => switchAllPaymentTypes(true)}
            disabled={allItemsCash}
            title="Switch every cart item to cash"
          >
            Switch all to Cash
          </button>
          <button
            type="button"
            className="switch-all-button switch-all-credit"
            onClick={() => switchAllPaymentTypes(false)}
            disabled={allItemsCredit}
            title="Switch every cart item to credit"
          >
            Switch all to Credit
          </button>
        </div>
      )}

      {/* Custom Buylist Items Button - hidden in checkout */}
      {!hideCheckoutButton && customItems.length > 0 && (
        <button
          className="custom-buylist-button"
          onClick={() => setShowCustomItems(!showCustomItems)}
        >
          {showCustomItems ? 'Hide Custom Items' : 'We Also Buy'}
        </button>
      )}
      
      {/* Custom Buylist Items Table */}
      {showCustomItems && customItems.length > 0 && (
        <div className="custom-buylist-container">
          <h3>Custom Items We Buy</h3>
          <table className="custom-buylist-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Description</th>
                <th>Cash Price</th>
                <th>Credit Price</th>
                <th>Quantity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customItems.map((item) => (
                <tr key={item._id.$oid}>
                  <td>{item.title}</td>
                  <td>{item.description}</td>
                  <td>{currencySymbol}{item.cash_price}</td>
                  <td>{currencySymbol}{item.credit_price}</td>
                  <td>
                    <input 
                      type="number" 
                      min="1" 
                      max="999" 
                      defaultValue="1"
                      className="custom-item-quantity"
                    />
                  </td>
                  <td>
                    <button 
                      className="add-custom-item-cash"
                      onClick={(e) => {
                        const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                        const customItem = {
                          name: item.title,
                          description: item.description,
                          cashPrice: item.cash_price,
                          creditPrice: item.credit_price,
                          quantity: quantity,
                          isCash: true,
                          cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                          condition: 'N/A',
                          printing: 'N/A',
                          isCustomItem: true
                        };
                        setCartItems(prevItems => [...prevItems, customItem]);
                      }}
                    >
                      Add (Cash)
                    </button>
                    <button 
                      className="add-custom-item-credit"
                      onClick={(e) => {
                        const quantity = parseInt(e.target.parentNode.parentNode.querySelector('.custom-item-quantity').value);
                        const customItem = {
                          name: item.title,
                          description: item.description,
                          cashPrice: item.cash_price,
                          creditPrice: item.credit_price,
                          quantity: quantity,
                          isCash: false,
                          cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                          condition: 'N/A',
                          printing: 'N/A',
                          isCustomItem: true
                        };
                        setCartItems(prevItems => [...prevItems, customItem]);
                      }}
                    >
                      Add (Credit)
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cartItems.length === 0 ? (
        <p className="cart-empty">Your cart is empty</p>
      ) : (
        <>
          <ul className="cart-items">
            {cartItems.map((item, index) => (
              <li key={item.cartItemId || `${item._id}-${index}`} className="cart-item">
                {/* Top Row: Remove, Thumbnail, Card Info */}
                <div className="cart-item-top">
                  <button
                    className="remove-button"
                    onClick={() => removeFromCart(item)}
                    aria-label={`Remove ${String(item.name || 'item')} from cart`}
                  >✕</button>

                  <div className="cart-item-thumbnail">
                    {item.image ? (
                      <img src={item.image} alt={String(item.name || 'Item')} />
                    ) : (
                      <div className="cart-item-thumbnail-placeholder">{String(item.name || 'U').charAt(0)}</div>
                    )}
                  </div>

                  <div className="cart-item-info">
                    <div className="item-name" title={String(item.name || '')}>{String(item.name || 'Unknown Item')}</div>
                    <div className="item-meta">
                      {item.expansionName && <span className="item-tag">{item.expansionName}</span>}
                      {item.number && <span className="item-tag">#{item.number}</span>}
                      {/* Video game items show variant and console instead of condition/printing */}
                      {item.isVideoGame ? (
                        <>
                          <span className="item-tag video-game">{item.variant?.toUpperCase() || 'CIB'}</span>
                          {item.consoleName && <span className="item-tag console">{item.consoleName}</span>}
                        </>
                      ) : (
                        <>
                          {!item.isCustomItem && <span className="item-tag">{item.condition}</span>}
                          {!item.isCustomItem && <span className="item-tag">{item.printing}</span>}
                        </>
                      )}
                      {item.language && item.language !== 'EN' && <span className="item-tag lang">{item.language}</span>}
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Price (quantity controls removed - each item is a separate line) */}
                <div className="cart-item-bottom">
                  <div
                    className={`price-box ${item.isCash ? 'cash' : 'credit'}`}
                    onClick={() => togglePaymentType(item)}
                    title={`Click to switch to ${item.isCash ? 'Credit' : 'Cash'}`}
                  >
                    <span className="price-value">{currencySymbol}{(item.isCash ? item.cashPrice : item.creditPrice).toFixed(2)}</span>
                    <span className="price-label">{item.isCash ? 'Cash' : 'Credit'}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="cart-summary">
            <div className="subtotal">
              <span>Cash Subtotal:</span>
              <span>{currencySymbol}{cashSubtotal.toFixed(2)}</span>
            </div>
            <div className="subtotal">
              <span>Credit Subtotal:</span>
              <span>{currencySymbol}{creditSubtotal.toFixed(2)}</span>
            </div>
            <div className="total">
              <span>Total:</span>
              <span>{currencySymbol}{total.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Split Section - Collapsible */}
          {canUsePaymentSplit && (
            <div className="payment-split-section">
              {/* Collapsible Header */}
              <div className="payment-split-header" onClick={togglePaymentSplit}>
                <h4 className="payment-split-title">
                  Payment Split {isPaymentSplitEnabled && '(Active)'}
                  <span
                    className="payment-split-info-icon"
                    title="Split your payment between cash and store credit. Slide towards cash to receive more in cash, or towards credit to receive more in store credit."
                    onClick={(e) => e.stopPropagation()}
                  >
                    ℹ️
                  </span>
                </h4>
                <span className={`collapse-icon ${isPaymentSplitExpanded ? 'expanded' : 'collapsed'}`}>
                  {isPaymentSplitExpanded ? '▼' : '▶'}
                </span>
              </div>

              {/* Collapsible Content - Only shown when expanded */}
              {isPaymentSplitExpanded && (
                <div className="payment-split-content">
                  {/* Slider Control */}
                  <div className="payment-split-slider">
                    <div className="slider-labels">
                      <span>Credit</span>
                      <span>Cash</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={cashPercentage}
                      onChange={handleCashPercentageChange}
                      className="payment-slider"
                    />

                    {/* Percentage Input */}
                    <div className="percentage-input-container">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={cashPercentage}
                        onChange={handleCashPercentageInputChange}
                        onBlur={handleCashPercentageInputBlur}
                        className="percentage-input"
                      />
                      <span className="percentage-symbol">%</span>
                      <span className="percentage-label">Cash</span>
                    </div>
                  </div>

                  {/* Dollar Amount Inputs */}
                  <div className="amount-inputs-container">
                    <div className="amount-input-group cash-input-group">
                      <label className="amount-input-label">Cash Amount:</label>
                      <div className="amount-input-wrapper">
                        <span className="currency-prefix">{currencySymbol}</span>
                        <input
                          type="number"
                          min="0"
                          max={totalCashPrice}
                          step="0.01"
                          value={cashAmountInput}
                          onChange={handleCashAmountChange}
                          onBlur={handleCashAmountBlur}
                          className="amount-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="amount-input-group credit-input-group">
                      <label className="amount-input-label">Credit Amount:</label>
                      <div className="amount-input-wrapper">
                        <span className="currency-prefix">{currencySymbol}</span>
                        <input
                          type="number"
                          min="0"
                          max={totalCreditPrice}
                          step="0.01"
                          value={creditAmountInput}
                          onChange={handleCreditAmountChange}
                          onBlur={handleCreditAmountBlur}
                          className="amount-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display Calculated Amounts */}
                  <div className="payment-split-amounts">
                    <div className="split-amount cash-amount">
                      <span className="amount-label">Cash Amount:</span>
                      <span className="amount-value">{currencySymbol}{splitCashAmount.toFixed(2)}</span>
                      <span className="amount-percentage">({cashPercentage.toFixed(1)}%)</span>
                    </div>
                    <div className="split-amount credit-amount">
                      <span className="amount-label">Credit Amount:</span>
                      <span className="amount-value">{currencySymbol}{splitCreditAmount.toFixed(2)}</span>
                      <span className="amount-percentage">({(100 - cashPercentage).toFixed(1)}%)</span>
                    </div>
                    <div className="split-total">
                      <span className="total-label">Split Total:</span>
                      <span className="total-value">{currencySymbol}{splitTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!hideCheckoutButton && (
            <button 
              className="checkout-button"
              onClick={checkout}
            >
              Proceed to Checkout
            </button>
          )}
          {onSaveForLater !== (() => {}) && (
            <button
              className="save-for-later-button"
              onClick={onSaveForLater}
            >
              Save for Later
            </button>
          )}
        </>
      )}

      {/* Cart Review Modal */}
      <CartReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        cartItems={cartItems}
        userSettings={userSettings}
        onSave={onSaveForLater}
      />
    </div>
  );
};

export default Cart;
